/**
 * Discipline KPI overlays for the 4D model.
 *
 * One overlay per schedule — Engineering, Procurement, Fabrication &
 * Construction, Pre-commissioning & Commissioning, plus the macro schedule
 * itself. Selecting an overlay colours the model by that schedule's progress
 * and lists the activities behind it; clicking an activity frames its objects.
 */
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Loader2, Layers, Eye, Crosshair, RefreshCw, Sparkles, ChevronRight, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { PlanActualDetail } from './DisciplinePlanActual';

const pct = (v) => (v == null ? '—' : `${Math.round(v * 10) / 10}%`);
const varClass = (v) => (v == null ? 'text-muted-foreground'
  : v < -5 ? 'text-rose-500' : v > 5 ? 'text-emerald-500' : 'text-amber-500');

export function DisciplineOverlayPanel({ modelId, scheduleId, projectId, viewerRef, onOverlayActive }) {
  const [summary, setSummary] = useState(null);
  const [active, setActive] = useState(null);
  const [overlay, setOverlay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [mode, setMode] = useState('PROGRESS');
  const [gate, setGate] = useState('');
  const [gates, setGates] = useState([]);
  const [cognara, setCognara] = useState(null);
  const [proposals, setProposals] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [drill, setDrill] = useState(null);

  // Direct Cognara links carry their own plan-vs-actual; a via-macro row has to
  // be resolved back to the discipline items hanging off that macro activity.
  const toggleRow = useCallback(async (a) => {
    if (expanded === a.activityId) { setExpanded(null); setDrill(null); return; }
    setExpanded(a.activityId);
    setDrill(null);
    if (a.source === 'COGNARA_DIRECT') return;
    try {
      const { data } = await api.get(`/macro-links/schedules/${scheduleId}/activities/${a.activityId}`);
      const d = (data.disciplines || []).find(x => x.key === active);
      setDrill({ measure: d?.measure || 'STAGE_GATE', links: d?.links || [] });
    } catch { setDrill({ measure: 'STAGE_GATE', links: [] }); }
  }, [expanded, scheduleId, active]);

  useEffect(() => {
    let dead = false;
    (async () => {
      if (!modelId || !scheduleId) { setLoadingSummary(false); return; }
      setLoadingSummary(true);
      try {
        const { data } = await api.get(`/macro-links/models/${modelId}/overlay-summary`,
          { params: { schedule_id: scheduleId } });
        if (!dead) setSummary(data.overlays || []);
      } catch { if (!dead) toast.error('Could not load the KPI overlays'); }
      finally { if (!dead) setLoadingSummary(false); }
    })();
    return () => { dead = true; };
  }, [modelId, scheduleId]);

  const apply = useCallback(async (discipline, nextMode = 'PROGRESS', nextGate = '') => {
    setLoading(true);
    setActive(discipline);
    setExpanded(null); setDrill(null);
    try {
      const params = { schedule_id: scheduleId, discipline, mode: nextMode };
      if (nextMode === 'GATE' && nextGate) params.gate = nextGate;
      const { data } = await api.get(`/macro-links/models/${modelId}/overlay`, { params });
      setOverlay(data);
      onOverlayActive?.(true);
      const painted = viewerRef?.current?.applyHotspot?.(
        (data.objects || []).map(o => ({ dbId: o.dbId, color: o.kpiColorHex })));
      if (!painted && (data.objects || []).length) {
        toast.message('Overlay ready — open the 3D view to see it painted on the model');
      }
      if (discipline !== 'MACRO') {
        try {
          const [g, st] = await Promise.all([
            api.get(`/macro-links/models/${modelId}/gates`, { params: { schedule_id: scheduleId, discipline } }),
            api.get('/cognara-4d-discipline/run-status', { params: { model_id: modelId, discipline } }),
          ]);
          setGates(g.data.gates || []);
          setCognara(st.data);
        } catch { /* the overlay still works without the gate list */ }
      } else { setGates([]); setCognara(null); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build that overlay');
    } finally { setLoading(false); }
  }, [modelId, scheduleId, viewerRef, onOverlayActive]);

  const changeMode = (m) => {
    setMode(m);
    if (active) apply(active, m, m === 'GATE' ? (gate || gates[0]?.name || '') : gate);
    if (m === 'GATE' && !gate && gates[0]) setGate(gates[0].name);
  };

  const changeGate = (g) => { setGate(g); if (active) apply(active, 'GATE', g); };

  const askCognara = async () => {
    if (!active || active === 'MACRO') return;
    setBusy(true);
    try {
      await api.post('/cognara-4d-discipline/propose', {}, {
        params: { project_id: projectId, model_id: modelId, schedule_id: scheduleId, discipline: active },
      });
      toast.success('Cognara is linking this schedule to the model — this takes a few minutes');
      const disc = active;
      const t = setInterval(async () => {
        try {
          const { data } = await api.get('/cognara-4d-discipline/run-status',
            { params: { model_id: modelId, discipline: disc } });
          setCognara(data);
          if (data.run?.status !== 'RUNNING') {
            clearInterval(t);
            if (data.pendingProposals) loadProposals(disc);
          }
        } catch { clearInterval(t); }
      }, 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start the linkage run');
    } finally { setBusy(false); }
  };

  const loadProposals = async (disc = active) => {
    try {
      const { data } = await api.get('/cognara-4d-discipline/proposals',
        { params: { model_id: modelId, discipline: disc, status: 'PENDING' } });
      setProposals(data.proposals || []);
    } catch { toast.error('Could not load the proposals'); }
  };

  const decide = async (proposalId, decision) => {
    try {
      await api.post(`/cognara-4d-discipline/proposals/${proposalId}/decision`, { decision });
      setProposals(p => (p || []).filter(x => x.proposalId !== proposalId));
      if (decision === 'ACCEPT') apply(active, mode, gate);
    } catch { toast.error('Could not record that decision'); }
  };

  const acceptAll = async () => {
    try {
      const { data } = await api.post('/cognara-4d-discipline/proposals/bulk-accept', { min_confidence: 0.8 },
        { params: { model_id: modelId, discipline: active } });
      toast.success(`${data.accepted} links accepted · ${data.objectsLinked} objects linked`);
      setProposals(null);
      apply(active, mode, gate);
    } catch { toast.error('Bulk accept failed'); }
  };

  const clear = () => {
    setActive(null); setOverlay(null);
    onOverlayActive?.(false);
    viewerRef?.current?.clearHotspot?.();
  };

  if (!scheduleId) {
    return (
      <div className="p-4 text-xs text-muted-foreground" data-testid="overlay-no-schedule">
        Link a schedule to this model first — the overlays are built from the macro activities and the
        Engineering / Procurement / F&C / Pre-comm activities attached to them.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="discipline-overlay-panel">
      <div className="px-3 py-2 border-b flex items-center gap-2 shrink-0">
        <Layers className="w-3.5 h-3.5 text-sky-400" />
        <span className="text-xs font-medium">Schedule KPI overlays</span>
        {active && (
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] ml-auto" onClick={clear}
                  data-testid="overlay-clear">
            Clear
          </Button>
        )}
      </div>

      {active && (
        <div className="px-3 py-2 border-b flex items-center gap-2 flex-wrap shrink-0" data-testid="overlay-controls">
          <div className="inline-flex rounded-md overflow-hidden border border-border">
            {[['PROGRESS', '%'], ['VARIANCE', 'Plan vs actual'], ...(gates.length ? [['GATE', 'Stage gate']] : [])]
              .map(([m, label]) => (
                <button key={m} onClick={() => changeMode(m)}
                        className={`px-2 py-1 text-[10px] ${mode === m ? 'bg-sky-600/80 text-white' : 'text-muted-foreground hover:bg-muted/50'}`}
                        data-testid={`overlay-mode-${m}`}>{label}</button>
              ))}
          </div>
          {mode === 'GATE' && gates.length > 0 && (
            <select value={gate} onChange={(e) => changeGate(e.target.value)}
                    className="h-6 text-[10px] bg-background border border-border rounded px-1"
                    data-testid="overlay-gate-picker">
              {gates.map(g => <option key={g.name} value={g.name}>{g.name} ({g.items})</option>)}
            </select>
          )}
          {overlay?.linkSource && (
            <Badge variant="outline" className="text-[9px]" data-testid="overlay-link-source">
              {overlay.linkSource === 'COGNARA_DIRECT' ? 'Cognara direct links' : 'via macro activity'}
            </Badge>
          )}
          {overlay?.linkSource === 'COGNARA_DIRECT' && cognara?.pendingProposals > 0 && (
            <span className="text-[9px] text-amber-400" data-testid="overlay-partial-hint">
              only the {overlay.activityCount} accepted link{overlay.activityCount === 1 ? ' is' : 's are'} shown —
              accept more to cover the rest
            </span>
          )}
          {active !== 'MACRO' && (
            <div className="ml-auto flex items-center gap-1">
              {cognara?.acceptedLinks > 0 && (
                <span className="text-[9px] text-emerald-400">{cognara.acceptedLinks} linked</span>
              )}
              {cognara?.pendingProposals > 0 && (
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-amber-400"
                        onClick={() => loadProposals()} data-testid="overlay-review-proposals">
                  Review {cognara.pendingProposals}
                </Button>
              )}
              <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-purple-400"
                      disabled={busy || cognara?.run?.status === 'RUNNING'} onClick={askCognara}
                      data-testid="overlay-ask-cognara">
                {cognara?.run?.status === 'RUNNING'
                  ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Linking…</>
                  : <><Sparkles className="w-3 h-3 mr-1" />Cognara link</>}
              </Button>
            </div>
          )}
        </div>
      )}

      {cognara?.run?.status === 'RUNNING' && cognara.run.phase && (
        <p className="px-3 py-1 text-[10px] text-muted-foreground" data-testid="overlay-cognara-phase">
          {cognara.run.phase}
        </p>
      )}

      {cognara?.run?.status === 'COMPLETE' && cognara.pendingProposals === 0 && cognara.acceptedLinks === 0 && (
        <div className="px-3 py-2 border-b border-border text-[10px] text-amber-400 space-y-1"
             data-testid="overlay-cognara-nothing">
          <p className="font-medium">
            Cognara found nothing it could credibly link in this register
            {cognara.run.itemsAnalysed ? ` (${cognara.run.itemsAnalysed} items read)` : ''}.
          </p>
          {(cognara.run.warnings || []).slice(0, 4).map((w, i) => (
            <p key={i} className="text-muted-foreground leading-snug">{w}</p>
          ))}
        </div>
      )}

      {proposals && (
        <div className="border-b border-border" data-testid="overlay-proposals">
          <div className="px-3 py-1.5 flex items-center gap-2 bg-muted/40">
            <span className="text-[11px] font-medium">{proposals.length} proposed link(s)</span>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-emerald-400 ml-auto"
                    onClick={acceptAll} data-testid="overlay-accept-all">Accept all ≥ 80%</Button>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]"
                    onClick={() => setProposals(null)} data-testid="overlay-proposals-close">Close</Button>
          </div>
          <ScrollArea className="max-h-56">
            {proposals.map((p) => (
              <div key={p.proposalId} className="px-3 py-1.5 border-t border-border/50 flex items-start gap-2 text-[11px]"
                   data-testid={`proposal-${p.proposalId}`}>
                <span className="font-mono text-[10px] text-sky-300 w-24 truncate">{p.code}</span>
                <span className="flex-1 min-w-0">
                  <span className="block truncate text-foreground" title={p.name}>{p.name}</span>
                  <span className="block text-[9px] text-muted-foreground truncate" title={p.rationale}>{p.rationale}</span>
                </span>
                <Badge variant="outline" className="text-[9px]">{p.proposedCount} obj</Badge>
                <Badge className="text-[9px]"
                       style={{ background: p.confidenceBand === 'HIGH' ? '#22C55E22' : p.confidenceBand === 'MEDIUM' ? '#F59E0B22' : '#94A3B822',
                                color: p.confidenceBand === 'HIGH' ? '#4ADE80' : p.confidenceBand === 'MEDIUM' ? '#FBBF24' : '#CBD5E1' }}>
                  {Math.round((p.confidence || 0) * 100)}%
                </Badge>
                <button onClick={() => decide(p.proposalId, 'ACCEPT')} className="text-emerald-400"
                        title="Accept" data-testid={`proposal-accept-${p.proposalId}`}>✓</button>
                <button onClick={() => decide(p.proposalId, 'REJECT')} className="text-red-400"
                        title="Reject" data-testid={`proposal-reject-${p.proposalId}`}>✕</button>
              </div>
            ))}
          </ScrollArea>
        </div>
      )}

      <div className="p-2 grid grid-cols-2 xl:grid-cols-3 gap-1.5 shrink-0">
        {loadingSummary && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground p-2 col-span-full">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />Reading the linked schedules…
          </div>
        )}
        {(summary || []).map((o) => (
          <button key={o.discipline}
                  onClick={() => apply(o.discipline)}
                  className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors border ${
                    active === o.discipline ? 'bg-muted/70' : 'bg-transparent hover:bg-muted/40'}`}
                  style={{ borderColor: active === o.discipline ? o.colour : 'var(--border)' }}
                  data-testid={`overlay-btn-${o.discipline}`}>
            <span className="w-1.5 h-7 rounded-sm shrink-0" style={{ background: o.colour }} />
            <span className="flex-1 min-w-0">
              <span className="block text-[10px] font-medium text-foreground truncate">{o.label}</span>
              <span className="block text-[9px] text-muted-foreground">
                {o.activityCount} act · {o.objectCount} obj
              </span>
            </span>
            <span className="text-xs font-semibold shrink-0" style={{ color: o.colour }}>
              {pct(o.averageProgressPct)}
            </span>
            {active === o.discipline && <Eye className="w-3 h-3 shrink-0" style={{ color: o.colour }} />}
          </button>
        ))}
      </div>

      {loading && (
        <div className="px-3 py-2 text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />Painting the model…
        </div>
      )}

      {overlay && (
        <>
          <div className="px-3 py-2 border-t border-b flex items-center gap-2 flex-wrap shrink-0"
               data-testid="overlay-legend">
            {(overlay.legend || []).map((l) => (
              <span key={l.label} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: l.colour }} />
                {l.label} <span className="opacity-60">({l.count})</span>
              </span>
            ))}
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] ml-auto"
                    onClick={() => apply(overlay.discipline)} data-testid="overlay-refresh">
              <RefreshCw className="w-3 h-3 mr-1" />Refresh
            </Button>
          </div>

          <ScrollArea className="flex-1 min-h-0">
            <table className="w-full text-[11px]" data-testid="overlay-activity-table">
              <thead className="text-muted-foreground uppercase text-[9px] sticky top-0 bg-background">
                <tr>{['', 'Code', 'Activity', 'Links', 'Plan', 'Actual', 'Var', 'Progress', ''].map((h, i) => (
                  <th key={`${h}-${i}`} className="text-left px-2 py-1 font-medium">{h}</th>))}</tr>
              </thead>
              <tbody>
                {(overlay.activities || []).map((a) => (
                  <Fragment key={a.activityId}>
                    <tr className="border-t border-border hover:bg-muted/40"
                        data-testid={`overlay-activity-${a.activityId}`}>
                      <td className="px-1 py-1.5">
                        {active !== 'MACRO' && (
                          <button className="text-muted-foreground hover:text-sky-300"
                                  onClick={() => toggleRow(a)} title="Plan vs actual detail"
                                  data-testid={`overlay-expand-${a.activityId}`}>
                            {expanded === a.activityId
                              ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          </button>
                        )}
                      </td>
                      <td className="px-2 py-1.5 font-mono text-[10px] text-sky-300">{a.activityCode}</td>
                      <td className="px-2 py-1.5 text-foreground max-w-[180px]">
                        <div className="truncate" title={a.activityName}>{a.activityName}</div>
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground">{a.linkCount}</td>
                      <td className="px-2 py-1.5 text-muted-foreground">{pct(a.plannedPct)}</td>
                      <td className="px-2 py-1.5 text-muted-foreground">{pct(a.actualPct)}</td>
                      <td className={`px-2 py-1.5 ${varClass(a.variancePct)}`}>{pct(a.variancePct)}</td>
                      <td className="px-2 py-1.5">
                        <Badge style={{ background: `${a.kpiColorHex}22`, color: a.kpiColorHex }}
                               className="text-[9px]">{pct(a.progressPct)}</Badge>
                      </td>
                      <td className="px-2 py-1.5">
                        {a.objectCount > 0 && (
                          <button className="text-muted-foreground hover:text-sky-300"
                                  title={`Frame ${a.objectCount} model object(s)`}
                                  onClick={() => {
                                    const ids = (overlay.objects || [])
                                      .filter(o => o.activityId === a.activityId).map(o => o.dbId);
                                    if (ids.length) viewerRef?.current?.zoomToDbIds?.(ids);
                                  }}
                                  data-testid={`overlay-locate-${a.activityId}`}>
                            <Crosshair className="w-3 h-3" />
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded === a.activityId && (
                      <tr className="bg-muted/20" data-testid={`overlay-detail-${a.activityId}`}>
                        <td colSpan={9} className="px-3 py-2">
                          {a.source === 'COGNARA_DIRECT' ? (
                            <PlanActualDetail measure={a.measure} pva={a.planVsActual} gates={a.stageGates} />
                          ) : !drill ? (
                            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                              <Loader2 className="w-3 h-3 animate-spin" />Reading the linked {overlay.disciplineLabel} items…
                            </div>
                          ) : !drill.links.length ? (
                            <p className="text-[10px] text-muted-foreground">
                              No {overlay.disciplineLabel} item is linked to this macro activity.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {drill.links.map((l) => (
                                <div key={l.microId} className="rounded-md border border-border p-2"
                                     data-testid={`overlay-drill-${l.microId}`}>
                                  <div className="flex items-center gap-2 mb-1.5">
                                    <span className="font-mono text-[10px] text-sky-300">{l.code}</span>
                                    <span className="text-[11px] truncate">{l.name}</span>
                                    <Badge variant="outline" className="text-[9px] h-4 px-1 ml-auto">
                                      {pct(l.progressPct)}
                                    </Badge>
                                  </div>
                                  <PlanActualDetail measure={drill.measure} pva={l.planVsActual}
                                                    gates={l.stageGates} />
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </>
      )}
    </div>
  );
}

export default DisciplineOverlayPanel;
